import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Blueprint } from '../../../../../models/blueprint.model';
import { BlueprintItem } from '../../../../../models/blueprint-item.model';
import { CadFile } from '../../../../../models/cad-file.model';
import {
  QuantityType,
  QuoteItemData,
} from '../../../../../models/quote-item-data.model';
import { CadObject } from '../../../../../models/cad-object.model';
import { QuantityPipe } from '../../../../../pipes/quantity.pipe';
import { consumableSelectionsFromLinks } from '../../../../../models/consumable.model';
import { QuoteService } from '../../../../quote.service';
import { CustomQuoteTree } from '../../../../../models/drawing-file.model';
import {
  applyBlueprintUnitCostDefaults,
  customTreeToQuoteItemData,
} from '../../../../blueprints-viewer/custom-quote-tree';
import { PricingModelService } from '../../../../services/pricing-model.service';

export interface QuoteItemDataPricingCadObject {
  /** CadObject numeric id, or drawing synthetic id (`drawing-{drawing_file_id}`). */
  id: number | string;
  name: string;
  inheritedFromName?: string;
}

@Injectable()
export class QuoteItemDataEditorService {
  private readonly quantityPipe = inject(QuantityPipe);
  private readonly quoteService = inject(QuoteService, { optional: true });
  private readonly pricingModelService = inject(PricingModelService, {
    optional: true,
  });
  private _items = signal<QuoteItemData[]>([]);
  public readonly items = this._items.asReadonly();

  /**
   * Items ready to send to the API. Blank names become Item 1, Item 2, etc.
   * to match the editor placeholder.
   */
  itemsForSubmit(): QuoteItemData[] {
    return this.items().map((item) => ({
      ...item,
      name: item.name?.trim() || `Item ${item.sibling_order + 1}`,
    }));
  }

  /**
   * Sorts siblings by sibling_order
   */
  sortSiblingsBySiblingOrder(items: QuoteItemData[]): QuoteItemData[] {
    return [...items].sort((a, b) => a.sibling_order - b.sibling_order);
  }

  draggingSubject = new BehaviorSubject<QuoteItemData | false>(false);
  public dragging$ = this.draggingSubject.asObservable();
  dragStart(item: QuoteItemData) {
    this.draggingSubject.next(item);
  }
  dragEnd() {
    this.draggingSubject.next(false);
  }
  drop(
    dropTarget: QuoteItemData,
    position: 'before' | 'endOfList' | 'child',
  ): void {
    const draggedItem = this.draggingSubject.value;
    if (!draggedItem) {
      throw new Error('No item is being dragged');
    }

    // Cancel if dropping on itself
    if (dropTarget.persistent_id === draggedItem.persistent_id) {
      this.dragEnd();
      return;
    }

    // Determine the new parent
    let newParentId: string | null = null;
    if (position === 'child') {
      newParentId = dropTarget.persistent_id;
    } else if (dropTarget.parent_id !== draggedItem.parent_id) {
      newParentId = dropTarget.parent_id;
    } else {
      newParentId = draggedItem.parent_id;
    }

    // Calculate the target index where the dragged item should be placed
    const targetIndex = this.calculateTargetIndex(
      draggedItem,
      dropTarget,
      position,
      newParentId,
    );

    // Get all items and find the dragged item from the current state
    const allItems = this.items();
    const currentDraggedItem = allItems.find(
      (item) => item.persistent_id === draggedItem.persistent_id,
    );
    if (!currentDraggedItem) {
      this.dragEnd();
      return;
    }

    // Get all siblings (excluding dragged item) and sort them
    const siblings = allItems
      .filter(
        (item) =>
          item.parent_id === newParentId &&
          item.persistent_id !== draggedItem.persistent_id,
      )
      .sort((a, b) => a.sibling_order - b.sibling_order);

    // Insert dragged item at the target index with updated parent_id
    siblings.splice(targetIndex, 0, {
      ...currentDraggedItem,
      parent_id: newParentId,
    });

    // Renumber all siblings to be consecutive (0, 1, 2, 3...)
    const updatedItems = allItems.map((item) => {
      const siblingIndex = siblings.findIndex(
        (s) => s.persistent_id === item.persistent_id,
      );
      if (siblingIndex >= 0) {
        // Only update parent_id for the dragged item
        if (item.persistent_id === draggedItem.persistent_id) {
          return {
            ...item,
            sibling_order: siblingIndex,
            parent_id: newParentId,
          };
        }
        return { ...item, sibling_order: siblingIndex };
      }
      return item;
    });

    this._items.set(updatedItems);

    this.dragEnd();
  }

  /**
   * Calculates the target index where the dragged item should be placed.
   * Returns the index in the sorted siblings array (0-based).
   */
  private calculateTargetIndex(
    draggedItem: QuoteItemData,
    dropTarget: QuoteItemData,
    position: 'before' | 'endOfList' | 'child',
    newParentId: string | null,
  ): number {
    const allItems = this.items();

    // Get all siblings at the new parent level (excluding the dragged item)
    const siblings = allItems
      .filter(
        (item) =>
          item.parent_id === newParentId &&
          item.persistent_id !== draggedItem.persistent_id,
      )
      .sort((a, b) => a.sibling_order - b.sibling_order);

    if (position === 'endOfList') {
      // Insert at the end
      return siblings.length;
    }

    if (position === 'before') {
      // Insert before dropTarget: find dropTarget's index
      const dropTargetIndex = siblings.findIndex(
        (s) => s.persistent_id === dropTarget.persistent_id,
      );
      if (dropTargetIndex >= 0) {
        return dropTargetIndex;
      }
      // Fallback: insert at position 0
      return 0;
    }

    // position === 'child'
    // Insert as a child: insert at the end
    return siblings.length;
  }

  /**
   * Updates an item's properties in the items array.
   * This ensures that updates are reflected in the service's state and propagated to all subscribers.
   *
   * @param item - The item to update (matched by persistent_id)
   * @param patch - Partial item data to update
   */
  updateItem(item: QuoteItemData, patch: Partial<QuoteItemData>): void {
    let items = this.items().map((i) => {
      if (i.persistent_id !== item.persistent_id) {
        return i;
      }

      const pricingModelChanged =
        'pricing_model_id' in patch &&
        i.pricing_model_id != null &&
        patch.pricing_model_id !== i.pricing_model_id;

      return {
        ...i,
        ...patch,
        pricing_parameters: pricingModelChanged
          ? null
          : 'pricing_parameters' in patch
            ? patch.pricing_parameters
            : i.pricing_parameters,
      };
    });

    const shouldNormalizePricingParameters =
      ('cad_object_ids' in patch &&
        !this.areNumberArraysEqual(
          patch.cad_object_ids ?? [],
          item.cad_object_ids ?? [],
        )) ||
      ('drawing_file_id' in patch &&
        (patch.drawing_file_id ?? null) !== (item.drawing_file_id ?? null)) ||
      ('pricing_model_id' in patch &&
        patch.pricing_model_id !== item.pricing_model_id);

    if (shouldNormalizePricingParameters) {
      items = this.normalizePricingParameters(items);
    }

    this._items.set(items);
  }

  getCadObjectsForPricing(
    item: QuoteItemData,
  ): QuoteItemDataPricingCadObject[] {
    return this.getCadObjectsForPricingFromItems(item, this.items());
  }

  private getCadObjectsForPricingFromItems(
    item: QuoteItemData,
    items: QuoteItemData[],
  ): QuoteItemDataPricingCadObject[] {
    if (item.cad_object_ids.length > 0) {
      return this.toPricingCadObjects(item);
    }

    let parentId = item.parent_id;
    while (parentId) {
      const parent = items.find(
        (candidate) => candidate.persistent_id === parentId,
      );
      if (!parent) {
        break;
      }
      if (parent.cad_object_ids.length > 0) {
        return this.toPricingCadObjects(parent, parent.name);
      }
      parentId = parent.parent_id;
    }

    if (item.drawing_file_id != null) {
      return this.toPricingDrawingObjects(item);
    }

    parentId = item.parent_id;
    while (parentId) {
      const parent = items.find(
        (candidate) => candidate.persistent_id === parentId,
      );
      if (!parent) {
        return [];
      }
      if (parent.drawing_file_id != null) {
        return this.toPricingDrawingObjects(parent, parent.name);
      }
      parentId = parent.parent_id;
    }

    return [];
  }

  private toPricingCadObjects(
    item: QuoteItemData,
    inheritedFromName?: string,
  ): QuoteItemDataPricingCadObject[] {
    return item.cad_object_ids.map((id, index) => ({
      id,
      name: item.cad_object_names?.[index] || `CAD object ${id}`,
      inheritedFromName,
    }));
  }

  private toPricingDrawingObjects(
    item: QuoteItemData,
    inheritedFromName?: string,
  ): QuoteItemDataPricingCadObject[] {
    if (item.drawing_file_id == null) {
      return [];
    }
    return [
      {
        id: `drawing-${item.drawing_file_id}`,
        name: item.drawing_file_name || `Drawing PDF ${item.drawing_file_id}`,
        inheritedFromName,
      },
    ];
  }

  private areNumberArraysEqual(first: number[], second: number[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSorted = [...first].sort((a, b) => a - b);
    const secondSorted = [...second].sort((a, b) => a - b);
    return firstSorted.every((value, index) => value === secondSorted[index]);
  }

  private normalizePricingParameters(items: QuoteItemData[]): QuoteItemData[] {
    return items.map((item) => {
      if (!item.pricing_parameters) {
        return item;
      }

      if (!item.pricing_model_id) {
        return item;
      }

      const effectiveCadIds = new Set(
        this.getCadObjectsForPricingFromItems(item, items).map((cad) =>
          String(cad.id),
        ),
      );
      if (effectiveCadIds.size === 0) {
        return { ...item, pricing_parameters: null };
      }

      const pricingParameters = Object.fromEntries(
        Object.entries(item.pricing_parameters).filter(([cadId]) =>
          effectiveCadIds.has(cadId),
        ),
      );
      if (Object.keys(pricingParameters).length === 0) {
        return { ...item, pricing_parameters: null };
      }

      return Object.keys(pricingParameters).length ===
        Object.keys(item.pricing_parameters).length
        ? item
        : { ...item, pricing_parameters: pricingParameters };
    });
  }

  /**
   * Creates a blank item and adds it to the tree
   */
  createBlankItem(
    name: string = '',
    parentId: string | null = null,
  ): QuoteItemData {
    const persistentId = this.generatePersistentId();
    const currentItems = this.items();

    const siblings = currentItems.filter((item) => item.parent_id === parentId);
    // Use consecutive ordering: 0, 1, 2, 3...
    const siblingOrder = siblings.length;

    const newItem: QuoteItemData = {
      persistent_id: persistentId,
      name: name,
      parent_id: parentId,
      sibling_order: siblingOrder,
      cad_object_ids: [],
    };

    const updatedItems = [...currentItems, newItem];
    this._items.set(updatedItems);

    return newItem;
  }

  buildBlueprintItemsForLinking(
    blueprint: Blueprint,
    allBlueprints: Blueprint[] = [],
    cadMappings?: Map<string, number[]>,
  ): QuoteItemData[] {
    const rootItem: QuoteItemData = {
      persistent_id: this.generatePersistentId(),
      name: blueprint.name,
      blueprint_id: blueprint.id,
      material_id: blueprint.default_material_id ?? null,
      inherit_material: false,
      parent_id: null,
      sibling_order: 0,
      cad_object_ids: cadMappings?.get('__root__') ?? [],
    };

    if (!blueprint.items?.length) {
      return [rootItem];
    }

    const rootBlueprintItems = blueprint.items.filter(
      (item) => item.parent_blueprint_item_id === null,
    );

    const childItems = this.expandBlueprintItems(
      rootBlueprintItems,
      rootItem.persistent_id,
      blueprint,
      allBlueprints,
      cadMappings,
      new Set([String(blueprint.id)]),
    );

    return [rootItem, ...childItems];
  }

  /**
   * Builds a list of QuoteItemData mirroring a CAD object hierarchy.
   * Each created item is pre-linked to its corresponding CAD object.
   */
  buildItemsFromCadObjects(cadObjects: CadObject[]): QuoteItemData[] {
    const items: QuoteItemData[] = [];

    const processObject = (
      cadObject: CadObject,
      parentId: string | null,
      siblingOrder: number,
    ): void => {
      const persistentId = this.generatePersistentId();
      items.push({
        persistent_id: persistentId,
        name: cadObject.name,
        parent_id: parentId,
        sibling_order: siblingOrder,
        cad_object_ids: [cadObject.id],
        cad_object_names: [cadObject.name],
      });

      if (cadObject.children?.length) {
        cadObject.children.forEach((child, idx) => {
          processObject(child, persistentId, idx);
        });
      }
    };

    cadObjects.forEach((cadObject, idx) => {
      processObject(cadObject, null, idx);
    });

    return items;
  }

  buildBulkItemsFromBlueprint(
    blueprint: Blueprint,
    allBlueprints: Blueprint[],
    cadFiles: CadFile[],
  ): QuoteItemData[] {
    const allItems: QuoteItemData[] = [];

    for (const cadFile of cadFiles) {
      const itemTree = this.buildBlueprintItemsForLinking(
        blueprint,
        allBlueprints,
      );

      const rootItem = itemTree.find((item) => item.parent_id === null);
      const rootCadObject = cadFile.cad_objects?.find(
        (obj) => obj.parent_id === null,
      );

      if (rootItem && rootCadObject) {
        rootItem.cad_object_ids = [rootCadObject.id];
        rootItem.cad_object_names = [rootCadObject.name];
        rootItem.name = rootCadObject.name;
      }

      allItems.push(...itemTree);
    }

    return allItems;
  }

  /**
   * Adds a blueprint to the editor without linking any CAD objects.
   *
   * The blueprint is fully expanded into concrete items (with empty
   * cad_object_ids) because the quote backend persists a flat item tree and
   * ignores blueprint references, so the structure must be materialised here.
   */
  addBlueprintWithoutCad(
    blueprint: Blueprint,
    allBlueprints: Blueprint[] = [],
  ): void {
    const preparedItems = this.buildBlueprintItemsForLinking(
      blueprint,
      allBlueprints,
    );
    this.addPreparedBlueprintItems(preparedItems);
  }

  /**
   * Expand a blueprint into the editor and attach a confirmed drawing PDF
   * to the top-level root item (no CAD link step).
   */
  addBlueprintWithDrawing(
    blueprint: Blueprint,
    allBlueprints: Blueprint[] = [],
    drawing: { id: number; filename: string },
  ): void {
    this.addPreparedItemsWithRootDrawing(
      this.buildBlueprintItemsForLinking(blueprint, allBlueprints),
      drawing,
    );
  }

  /**
   * Expand the same blueprint once per confirmed drawing for bulk PDF upload.
   */
  addBlueprintWithDrawings(
    blueprint: Blueprint,
    allBlueprints: Blueprint[] = [],
    drawings: { id: number; filename: string }[],
  ): void {
    for (const drawing of drawings) {
      this.addBlueprintWithDrawing(blueprint, allBlueprints, drawing);
    }
  }

  /**
   * Materialise an AI custom quote tree and attach a confirmed drawing PDF
   * to the top-level root only (same attachment rules as blueprint and drawing).
   * Reuses unambiguous positive unit costs from loaded tenant blueprints.
   */
  addCustomTreeWithDrawing(
    customTree: CustomQuoteTree,
    drawing: { id: number; filename: string },
    allBlueprints: Blueprint[] = [],
  ): void {
    if (!customTree.root) {
      return;
    }
    const preparedItems = customTreeToQuoteItemData(customTree.root, {
      createPersistentId: () => this.generatePersistentId(),
    });
    if (!preparedItems.length) {
      return;
    }
    this.addPreparedItemsWithRootDrawing(
      applyBlueprintUnitCostDefaults(preparedItems, allBlueprints, {
        convertUnitCost: (unitCost) =>
          this.convertBlueprintUnitCostToDisplay(unitCost),
        modelOutputsUnitCost: (pricingModelId) =>
          this.pricingModelService?.pricingModelsById()?.[pricingModelId]
            ?.output_unit_cost === true,
      }),
      drawing,
    );
  }

  /**
   * Add a single root item linked to a confirmed drawing (no blueprint template).
   */
  addDrawingWithoutBlueprint(drawing: {
    id: number;
    filename: string;
  }): QuoteItemData {
    const item = this.createBlankItem(
      this.displayNameFromDrawingFilename(drawing.filename),
    );
    item.drawing_file_id = drawing.id;
    item.drawing_file_name = drawing.filename;
    item.cad_object_ids = [];
    item.cad_object_names = [];
    this._items.set(
      this.items().map((existing) =>
        existing.persistent_id === item.persistent_id ? { ...item } : existing,
      ),
    );
    return item;
  }

  /**
   * Add one blank root item per confirmed drawing aka multi-PDF skip path.
   */
  addDrawingsWithoutBlueprint(
    drawings: { id: number; filename: string }[],
  ): QuoteItemData[] {
    return drawings.map((drawing) => this.addDrawingWithoutBlueprint(drawing));
  }

  private displayNameFromDrawingFilename(filename: string): string {
    const base =
      filename.trim().split(/[/\\]/).pop()?.trim() || filename.trim();
    const withoutExtension = base.replace(/\.[^.]+$/, '');
    return withoutExtension || base;
  }

  /**
   * Attach a confirmed drawing to the prepared tree root, then add the tree.
   * The root takes the file/part name, matching CAD blueprint linking.
   */
  private addPreparedItemsWithRootDrawing(
    preparedItems: QuoteItemData[],
    drawing: { id: number; filename: string },
  ): void {
    const rootItem = preparedItems.find((item) => item.parent_id === null);
    if (rootItem) {
      rootItem.drawing_file_id = drawing.id;
      rootItem.drawing_file_name = drawing.filename;
      rootItem.name = this.displayNameFromDrawingFilename(drawing.filename);
      rootItem.cad_object_ids = [];
      rootItem.cad_object_names = [];
    }
    this.addPreparedBlueprintItems(preparedItems);
  }

  addPreparedBlueprintItems(preparedItems: QuoteItemData[]): void {
    if (!preparedItems.length) {
      return;
    }

    const currentItems = this.items();
    const rootItems = preparedItems
      .filter((item) => item.parent_id === null)
      .sort((a, b) => a.sibling_order - b.sibling_order);

    const rootSiblingStart = currentItems.filter(
      (item) => item.parent_id === null,
    ).length;

    const rootSiblingMap = new Map<string, number>();
    rootItems.forEach((item, index) => {
      rootSiblingMap.set(item.persistent_id, rootSiblingStart + index);
    });

    const normalizedItems = preparedItems.map((item) => {
      if (item.parent_id !== null) {
        return item;
      }

      return {
        ...item,
        sibling_order:
          rootSiblingMap.get(item.persistent_id) ?? item.sibling_order,
      };
    });

    this._items.set([...currentItems, ...normalizedItems]);
  }

  /**
   * Expands a blueprint and adds its items to the service's items array.
   * Used when loading nested blueprints on-demand.
   */
  expandBlueprintAndAddItems(
    blueprint: Blueprint,
    parentId: string,
    allBlueprints: Blueprint[],
  ): void {
    // Prevent duplicate expansion
    if (this.items().some((item) => item.parent_id === parentId)) {
      return;
    }

    if (!blueprint.items?.length) {
      return;
    }

    const rootItems = blueprint.items.filter(
      (item) => item.parent_blueprint_item_id === null,
    );

    const expandedItems = this.expandBlueprintItems(
      rootItems,
      parentId,
      blueprint,
      allBlueprints,
    );

    this._items.set([...this.items(), ...expandedItems]);
  }

  /**
   * Maps a blueprint item's material settings to quote-item fields when expanding
   * a blueprint into the editor tree.
   *
   * Linked items use the linked blueprint's default material (not the parent
   * blueprint's), so nested blueprints keep their own material context — e.g.
   * Pattern (Medium Density Epoxy) inside Composite Mould (CFRP).
   *
   * All other items pass through inherit_material; when true, material_id is
   * left null so the quote tree recalculator can propagate from the parent.
   */
  private resolveBlueprintItemMaterial(
    blueprintItem: BlueprintItem,
    allBlueprints: Blueprint[],
  ): Pick<QuoteItemData, 'material_id' | 'inherit_material'> {
    if (blueprintItem.linked_to_blueprint_id && allBlueprints.length > 0) {
      const linkedBlueprint = allBlueprints.find(
        (blueprint) =>
          String(blueprint.id) === String(blueprintItem.linked_to_blueprint_id),
      );
      if (linkedBlueprint?.default_material_id != null) {
        return {
          material_id: linkedBlueprint.default_material_id,
          inherit_material: false,
        };
      }
    }

    return {
      material_id: blueprintItem.inherit_material
        ? null
        : (blueprintItem.material_id ?? null),
      inherit_material: blueprintItem.inherit_material ?? false,
    };
  }

  private convertBlueprintUnitCostToDisplay(unitCost: string): string {
    const exchangeRateRaw = this.quoteService?.revision?.exchange_rate;
    const parsedCost = Number(unitCost);
    const parsedRate = Number(exchangeRateRaw);
    if (
      !Number.isFinite(parsedCost) ||
      !Number.isFinite(parsedRate) ||
      parsedRate <= 0
    ) {
      return unitCost;
    }
    return (parsedCost * parsedRate).toFixed(2);
  }

  /**
   * Expands BlueprintItems recursively, creating editable items.
   * Recursively expands nested blueprints.
   */
  private expandBlueprintItems(
    blueprintItems: BlueprintItem[],
    parentId: string,
    blueprint: Blueprint,
    allBlueprints: Blueprint[],
    cadMappings?: Map<string, number[]>,
    visitedBlueprintIds?: Set<string>,
  ): QuoteItemData[] {
    const items: QuoteItemData[] = [];
    const sortedItems = [...blueprintItems].sort(
      (a, b) => a.sibling_order - b.sibling_order,
    );

    for (let idx = 0; idx < sortedItems.length; idx++) {
      const blueprintItem = sortedItems[idx];
      const persistentId = this.generatePersistentId();
      const mappingKey = this.getBlueprintMappingKey(
        blueprint.id,
        blueprintItem.id,
      );

      const { material_id, inherit_material } =
        this.resolveBlueprintItemMaterial(blueprintItem, allBlueprints);

      const quantityType = blueprintItem.quantity_type ?? QuantityType.UNIT;

      const itemData: QuoteItemData = {
        persistent_id: persistentId,
        name: blueprintItem.name,
        quantity: this.quantityPipe.transform(
          { quantity: blueprintItem.quantity, quantity_type: quantityType },
          'input',
        ),
        unit_cost: this.convertBlueprintUnitCostToDisplay(
          blueprintItem.unit_cost,
        ),
        batch_size: blueprintItem.batch_size ?? undefined,
        markup_percentage: blueprintItem.markup_percentage,
        quantity_type: quantityType,
        material_id,
        inherit_material,
        tax_code_id: blueprintItem.tax_code_id ?? null,
        blueprint_id: blueprintItem.linked_to_blueprint_id || undefined,
        parent_id: parentId,
        sibling_order: idx,
        cad_object_ids: cadMappings?.get(mappingKey) ?? [],
        capex_equipment_ids:
          (blueprintItem.capex_equipment_ids?.length ?? 0) > 0
            ? blueprintItem.capex_equipment_ids
            : (blueprintItem.capex_equipment?.map(
                (equipment) => equipment.id,
              ) ?? []),
        consumables: consumableSelectionsFromLinks(blueprintItem.consumables),
        pricing_model_id: blueprintItem.pricing_model_id || undefined,
      };
      items.push(itemData);

      if (blueprintItem.linked_to_blueprint_id && allBlueprints.length > 0) {
        const linkedBlueprintId = String(blueprintItem.linked_to_blueprint_id);
        if (visitedBlueprintIds?.has(linkedBlueprintId)) {
          continue;
        }

        const nestedBlueprint = allBlueprints.find(
          (b) => String(b.id) === String(blueprintItem.linked_to_blueprint_id),
        );

        if (nestedBlueprint?.items?.length) {
          const nestedVisited = new Set(visitedBlueprintIds ?? []);
          nestedVisited.add(String(nestedBlueprint.id));

          const nestedRootItems = nestedBlueprint.items.filter(
            (item) => item.parent_blueprint_item_id === null,
          );
          items.push(
            ...this.expandBlueprintItems(
              nestedRootItems,
              persistentId,
              nestedBlueprint,
              allBlueprints,
              cadMappings,
              nestedVisited,
            ),
          );
        }
      } else {
        const children =
          blueprint.items?.filter(
            (item) =>
              String(item.parent_blueprint_item_id) ===
              String(blueprintItem.id),
          ) || [];

        if (children.length > 0) {
          items.push(
            ...this.expandBlueprintItems(
              children,
              persistentId,
              blueprint,
              allBlueprints,
              cadMappings,
              visitedBlueprintIds,
            ),
          );
        }
      }
    }

    return items;
  }

  private getBlueprintMappingKey(
    blueprintId: string,
    blueprintItemId?: string,
  ): string {
    return `${blueprintId}:${blueprintItemId ?? ''}`;
  }

  /**
   * Removes an item and all its children recursively
   */
  removeItem(itemPersistentId: string): void {
    const currentItems = this.items();
    const idsToRemove = new Set<string>();
    idsToRemove.add(itemPersistentId);

    const findChildren = (parentId: string): void => {
      const children = currentItems.filter(
        (item) => item.parent_id === parentId,
      );
      children.forEach((child) => {
        idsToRemove.add(child.persistent_id);
        findChildren(child.persistent_id);
      });
    };

    findChildren(itemPersistentId);

    const updatedItems = currentItems.filter(
      (item) => !idsToRemove.has(item.persistent_id),
    );

    this._items.set(updatedItems);
  }

  /**
   * Clears all items
   */
  clearItems(): void {
    this._items.set([]);
  }

  /**
   * Generates a unique persistent ID
   */
  private generatePersistentId(): string {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    return `local-${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }
}
